import type { EChartsCoreOption } from 'echarts/core';
import { BIAS_GRAPH_BLUE, BIAS_GRAPH_GREEN, BIAS_GRAPH_RED } from '../Bias/biasLlmChartPalette';
import {
  countByKey,
  getPDCScoreData,
  readPDCExplainabilityEvidence,
  readPDCFindingsEvidence,
  readPDCFindingsKpis,
  type PDCRun
} from './pdc.utils';

const AXIS_LABEL = '#64748b';
const AXIS_LINE = '#e2e8f0';
const SPLIT_LINE = '#f0f0f0';
const REVIEW_AMBER = '#f59e0b';
const PIE_COLORS = [BIAS_GRAPH_RED, REVIEW_AMBER, BIAS_GRAPH_GREEN, BIAS_GRAPH_BLUE, '#8b5cf6', '#64748b'];

type ChartColors = {
  dangerColor: string;
  primaryColor: string;
  bodyHighlightBg: string;
  borderColor: string;
  lightTextEmphasis: string;
};

function baseTooltip(colors: ChartColors) {
  return {
    backgroundColor: colors.bodyHighlightBg,
    borderColor: colors.borderColor,
    textStyle: { color: colors.lightTextEmphasis }
  };
}

export function getRiskFactorDistributionOption(run: PDCRun | null, colors: ChartColors): EChartsCoreOption | null {
  const scores = getPDCScoreData(run);
  if (!scores) return null;

  const categories = ['Injection', 'Jailbreak', 'Leakage', 'Exfiltration', 'Obfuscation', 'Output Risk'];
  const values = [
    scores.injectionScore,
    scores.jailbreakScore,
    scores.leakageScore,
    scores.exfiltrationScore,
    scores.obfuscationScore,
    scores.outputRiskScore
  ];

  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, ...baseTooltip(colors) },
    grid: { left: '3%', right: '8%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'value',
      min: 0,
      max: 1,
      name: 'Score (0–1)',
      axisLabel: { color: AXIS_LABEL },
      axisLine: { lineStyle: { color: AXIS_LINE } },
      splitLine: { lineStyle: { color: SPLIT_LINE } }
    },
    yAxis: {
      type: 'category',
      data: categories,
      axisLabel: { color: AXIS_LABEL },
      axisLine: { lineStyle: { color: AXIS_LINE } }
    },
    series: [
      {
        type: 'bar',
        data: values.map(v => ({
          value: v,
          itemStyle: { color: v >= 0.7 ? colors.dangerColor : v >= 0.4 ? REVIEW_AMBER : colors.primaryColor }
        })),
        label: { show: true, position: 'right', formatter: (p: { value: number }) => Number(p.value).toFixed(3) }
      }
    ]
  };
}

export function getAttackCategoryPieOption(run: PDCRun | null, colors: ChartColors): EChartsCoreOption | null {
  const evidence = readPDCFindingsEvidence(run);
  if (evidence.length === 0) return null;

  const counts = countByKey(evidence, row => String(row.component ?? row.attack_type ?? 'Unknown'));
  const entries = Object.entries(counts);
  if (entries.length === 0) return null;

  return {
    color: PIE_COLORS,
    tooltip: { trigger: 'item', ...baseTooltip(colors) },
    legend: { bottom: 0, textStyle: { color: AXIS_LABEL } },
    series: [
      {
        type: 'pie',
        radius: ['40%', '68%'],
        data: entries.map(([name, value]) => ({ name, value })),
        label: { color: AXIS_LABEL }
      }
    ]
  };
}

export function getRootCauseDistributionOption(run: PDCRun | null, colors: ChartColors): EChartsCoreOption | null {
  const evidence = readPDCExplainabilityEvidence(run);
  if (evidence.length === 0) return null;

  const counts: Record<string, number> = {};
  for (const row of evidence) {
    const primary = (row.primary_root_cause as { category?: string } | undefined)?.category?.trim();
    if (primary) counts[primary] = (counts[primary] ?? 0) + 1;
    const secondary = row.secondary_root_causes;
    if (Array.isArray(secondary)) {
      for (const sec of secondary) {
        const cat = (sec as { category?: string })?.category?.trim();
        if (cat) counts[cat] = (counts[cat] ?? 0) + 1;
      }
    }
  }

  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;

  return {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, ...baseTooltip(colors) },
    grid: { left: '3%', right: '8%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'value',
      min: 0,
      axisLabel: { color: AXIS_LABEL },
      axisLine: { lineStyle: { color: AXIS_LINE } },
      splitLine: { lineStyle: { color: SPLIT_LINE } }
    },
    yAxis: {
      type: 'category',
      data: entries.map(([k]) => k),
      axisLabel: { color: AXIS_LABEL, width: 140, overflow: 'truncate' },
      axisLine: { lineStyle: { color: AXIS_LINE } }
    },
    series: [
      {
        type: 'bar',
        data: entries.map(([, v]) => ({ value: v, itemStyle: { color: colors.primaryColor } })),
        label: { show: true, position: 'right' }
      }
    ]
  };
}

export function getEvidenceConfidenceOption(run: PDCRun | null, colors: ChartColors): EChartsCoreOption | null {
  const evidence = readPDCFindingsEvidence(run);
  if (evidence.length === 0) return null;

  const ids = evidence.map(row => String(row.finding_id ?? ''));
  const confidences = evidence.map(row => Number(row.confidence ?? 0));

  return {
    tooltip: { trigger: 'axis', ...baseTooltip(colors) },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'category',
      data: ids,
      axisLabel: { color: AXIS_LABEL, rotate: ids.length > 6 ? 30 : 0 },
      axisLine: { lineStyle: { color: AXIS_LINE } }
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 1,
      name: 'Confidence',
      axisLabel: { color: AXIS_LABEL },
      axisLine: { lineStyle: { color: AXIS_LINE } },
      splitLine: { lineStyle: { color: SPLIT_LINE } }
    },
    series: [
      {
        type: 'bar',
        data: confidences.map(v => ({
          value: v,
          itemStyle: { color: v >= 0.7 ? colors.dangerColor : colors.primaryColor }
        })),
        label: { show: true, position: 'top', formatter: (p: { value: number }) => Number(p.value).toFixed(3) }
      }
    ]
  };
}

export function getBlastRadiusRadarOption(run: PDCRun | null, colors: ChartColors): EChartsCoreOption | null {
  const scores = getPDCScoreData(run);
  if (!scores) return null;

  const indicators = [
    { name: 'Business Impact', max: 1 },
    { name: 'Compliance Impact', max: 1 },
    { name: 'Operational Impact', max: 1 },
    { name: 'Data Exposure Risk', max: 1 }
  ];
  const values = [
    scores.businessImpactScore,
    scores.complianceImpactScore,
    scores.operationalImpactScore,
    scores.dataExposureRiskScore
  ];

  return {
    tooltip: baseTooltip(colors),
    radar: { indicator: indicators, axisName: { color: AXIS_LABEL } },
    series: [
      {
        type: 'radar',
        data: [{ value: values, name: 'Impact Profile', areaStyle: { opacity: 0.25 } }],
        itemStyle: { color: colors.dangerColor }
      }
    ]
  };
}

export function getImpactSeverityPieOption(run: PDCRun | null, colors: ChartColors): EChartsCoreOption | null {
  const evidence = readPDCFindingsEvidence(run);
  if (evidence.length === 0) return null;

  const counts: Record<string, number> = {};
  for (const row of evidence) {
    const impact = row.impact;
    if (!impact || typeof impact !== 'object') continue;
    for (const val of Object.values(impact)) {
      const key = String(val ?? '').trim();
      if (key) counts[key] = (counts[key] ?? 0) + 1;
    }
  }

  const entries = Object.entries(counts);
  if (entries.length === 0) return null;

  return {
    color: PIE_COLORS,
    tooltip: { trigger: 'item', ...baseTooltip(colors) },
    legend: { bottom: 0, textStyle: { color: AXIS_LABEL } },
    series: [
      {
        type: 'pie',
        radius: ['40%', '68%'],
        data: entries.map(([name, value]) => ({ name, value })),
        label: { color: AXIS_LABEL }
      }
    ]
  };
}

export function getSeverityDistributionOption(run: PDCRun | null, colors: ChartColors): EChartsCoreOption | null {
  const kpis = readPDCFindingsKpis(run);
  const values = [
    Number(kpis.critical_findings ?? 0),
    Number(kpis.high_findings ?? 0),
    Number(kpis.medium_findings ?? 0),
    Number(kpis.low_findings ?? 0)
  ];

  if (values.every(v => v === 0)) return null;

  const barColors = [BIAS_GRAPH_RED, REVIEW_AMBER, BIAS_GRAPH_BLUE, BIAS_GRAPH_GREEN];

  return {
    tooltip: { trigger: 'axis', ...baseTooltip(colors) },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'category',
      data: ['Critical', 'High', 'Medium', 'Low'],
      axisLabel: { color: AXIS_LABEL },
      axisLine: { lineStyle: { color: AXIS_LINE } }
    },
    yAxis: {
      type: 'value',
      min: 0,
      name: 'Findings Count',
      axisLabel: { color: AXIS_LABEL },
      axisLine: { lineStyle: { color: AXIS_LINE } },
      splitLine: { lineStyle: { color: SPLIT_LINE } }
    },
    series: [
      {
        type: 'bar',
        data: values.map((v, i) => ({ value: v, itemStyle: { color: barColors[i] } })),
        label: { show: true, position: 'top' }
      }
    ]
  };
}

export function getComplianceWaterfallOption(run: PDCRun | null, colors: ChartColors): EChartsCoreOption | null {
  const scores = getPDCScoreData(run);
  if (!scores) return null;

  const baseScore = 1.0;
  const corePenalty = scores.detectionScore;
  const blastPenalty = scores.businessImpactScore;
  const outputPenalty = scores.outputRiskScore;
  const explainPenalty = Math.max(0, 1.0 - scores.explainabilityScore);
  const finalScore = scores.defenseScore;

  const steps = [
    { name: 'Base Score', value: baseScore, color: BIAS_GRAPH_GREEN },
    { name: 'Core Risk Penalty', value: -corePenalty, color: BIAS_GRAPH_RED },
    { name: 'Blast Radius Penalty', value: -blastPenalty, color: REVIEW_AMBER },
    { name: 'Output Risk Penalty', value: -outputPenalty, color: BIAS_GRAPH_RED },
    { name: 'Explainability Penalty', value: -explainPenalty, color: REVIEW_AMBER },
    { name: 'Final Score', value: finalScore, color: BIAS_GRAPH_BLUE }
  ];

  let cumulative = 0;
  const placeholder: number[] = [];
  const barData = steps.map(step => {
    if (step.name === 'Base Score') {
      placeholder.push(0);
      cumulative = step.value;
      return { value: step.value, itemStyle: { color: step.color } };
    }
    if (step.name === 'Final Score') {
      placeholder.push(0);
      return { value: step.value, itemStyle: { color: step.color } };
    }
    const offset = cumulative + step.value;
    placeholder.push(Math.min(cumulative, offset));
    cumulative = offset;
    return { value: Math.abs(step.value), itemStyle: { color: step.color } };
  });

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      ...baseTooltip(colors),
      formatter: (params: unknown) => {
        const items = Array.isArray(params) ? params : [params];
        const idx = (items[1] as { dataIndex?: number })?.dataIndex ?? 0;
        const step = steps[idx];
        if (!step) return '';
        if (step.name === 'Final Score') return `Final Score: ${finalScore.toFixed(3)}`;
        if (step.name === 'Base Score') return `Base Score: ${baseScore.toFixed(3)}`;
        return `${step.name}: −${Math.abs(step.value).toFixed(3)}`;
      }
    },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'category',
      data: steps.map(s => s.name.replace(' Penalty', '')),
      axisLabel: { color: AXIS_LABEL, rotate: 20, fontSize: 10 },
      axisLine: { lineStyle: { color: AXIS_LINE } }
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 1,
      name: 'Defense Score',
      axisLabel: { color: AXIS_LABEL },
      axisLine: { lineStyle: { color: AXIS_LINE } },
      splitLine: { lineStyle: { color: SPLIT_LINE } }
    },
    series: [
      {
        name: 'Placeholder',
        type: 'bar',
        stack: 'waterfall',
        itemStyle: { borderColor: 'transparent', color: 'transparent' },
        emphasis: { itemStyle: { borderColor: 'transparent', color: 'transparent' } },
        data: placeholder
      },
      {
        name: 'Score',
        type: 'bar',
        stack: 'waterfall',
        data: barData,
        label: {
          show: true,
          position: 'top',
          formatter: (p: { dataIndex: number; value: number }) => {
            const step = steps[p.dataIndex];
            if (step?.name === 'Final Score') return finalScore.toFixed(3);
            if (step?.name === 'Base Score') return baseScore.toFixed(3);
            return `−${Number(p.value).toFixed(3)}`;
          }
        }
      }
    ]
  };
}
